import type { Classe, Emplacement, ObjetDTO, Stat } from '@whatremains/shared'
import {
  ARTICLE_FAMILLE, CLASSE_INFO, MAINS, MAINS_PAR_CLASSE,
  NOM_EMPLACEMENT, NOM_FAMILLE, NOM_PALIER, PRISE,
} from '@whatremains/shared'
import { ABREGES } from './Comparaison'
import { IconeObjet } from './IconeObjet'

/**
 * Ce qu'une pièce est, et ce qu'on peut en faire.
 *
 * Enzo, en jouant : « j'ai une canne à chaîner. Je ne sais pas ce que c'est. Je
 * ne sais pas si c'est pour moi. Je ne sais même pas où ça se met, et quand je
 * clique il ne se passe rien. Il y a beaucoup d'informations qui manquent sur
 * les objets. »
 *
 * ## Ce qu'il manquait, point par point
 *
 * La liste montrait un nom coloré, l'emplacement et un écart de statistiques.
 * Six choses n'étaient nulle part :
 *
 *   - **le palier en toutes lettres.** Il n'existait que comme couleur de
 *     contour, c'est-à-dire un code de huit teintes que le jeu n'explique jamais ;
 *   - **ce que la pièce EST.** « Canne à chaîner » ne dit pas « bâton », et
 *     c'est pourtant le mot qui décide si l'on peut la tenir ;
 *   - **à une main ou à deux**, donc si la main gauche reste libre ;
 *   - **les dégâts.** Le premier chiffre qui départage deux armes, et il ne
 *     sortait pas du serveur ;
 *   - **la description**, écrite pour les 155 modèles et affichée nulle part ;
 *   - **et pourquoi ça ne marche pas.** C'était le vrai sujet : la Canne à
 *     chaîner est un bâton, un rogue ne tient pas de bâton, donc le bouton ne
 *     pouvait rien faire. Le jeu restait muet plutôt que de le dire.
 *
 * ## L'empêchement se dit toujours
 *
 * Une action refusée sans raison est un bogue du point de vue du joueur, même
 * quand le serveur a raison de refuser. Ici la raison est écrite avant le clic,
 * pas après : niveau trop bas, ou classe qui ne manie pas cette famille.
 *
 * C'est la même règle que pour les quêtes — `08-INTERFACE.md` §6, « le mystère
 * est dans le monde, jamais dans la logistique ». Une pièce qu'on ne peut pas
 * porter n'est pas un mystère, c'est une contrainte, et une contrainte s'énonce.
 */

/**
 * Comment nommer une destination quand la pièce en a plusieurs.
 *
 * `NOM_EMPLACEMENT` sert la colonne de l'inventaire et parle de la PIÈCE
 * (« Arme », « Main gauche (moitié) ») ; sur un bouton il faut parler du
 * GESTE, court et sans parenthèse. Les emplacements à destination unique n'y
 * figurent pas : leur bouton dit « Porter » et n'a rien à préciser.
 */
const OU_PORTER: Partial<Record<Emplacement, string>> = {
  arme_principale: 'main droite',
  arme_secondaire: 'main gauche',
  anneau_1: 'premier doigt',
  anneau_2: 'second doigt',
}

/**
 * Ce qui empêche de porter la pièce, ou `null` si rien n'empêche.
 *
 * Deux formulations, et les deux servent :
 *
 *   - `court` tient sur la ligne de liste, à la place du bouton. Il doit dire
 *     LAQUELLE des raisons s'applique — un « ne se porte pas » identique sur
 *     toutes les lignes ne vaut pas mieux que le bouton muet qu'il remplace,
 *     puisqu'on ne sait toujours pas s'il faut monter de niveau ou changer de
 *     pièce ;
 *   - `phrase` s'écrit en toutes lettres dans la fiche, où il y a la place.
 */
export function empechement(
  o: ObjetDTO,
  classe: Classe,
  niveau: number,
): { court: string; phrase: string } | null {
  if (o.categorie !== 'equipement') {
    return {
      court: 'ne s’équipe pas',
      phrase: 'Ça ne se porte pas : ça se vend, ou ça sert à forger.',
    }
  }
  /*
   * La liste n'est vide que pour une pièce de main : `emplacementsPossibles()`
   * rend toujours son emplacement aux autres. Donc c'est toujours une question
   * de classe, et c'est le cas exact de la Canne à chaîner d'Enzo — un bâton,
   * qu'un rogue ne tient pas.
   */
  if (o.emplacementsPossibles.length === 0) {
    const nom = NOM_FAMILLE[o.famille].toLowerCase()
    const cl = CLASSE_INFO[classe].nom.toLowerCase()
    return {
      court: `pas pour un ${cl}`,
      phrase: `Un ${cl} ne manie pas ${ARTICLE_FAMILLE[o.famille]}${nom}.`,
    }
  }
  if (niveau < o.niveauRequis) {
    return {
      court: `niveau ${o.niveauRequis}`,
      phrase: `Il te faut le niveau ${o.niveauRequis}. Tu es niveau ${niveau}.`,
    }
  }
  return null
}

export function FicheObjet({
  o,
  classe,
  niveau,
  surPorter,
  surRanger,
  surConsommer,
  surFermer,
}: {
  o: ObjetDTO
  classe: Classe
  niveau: number
  surPorter?: (emplacement: string) => void
  surRanger?: () => void
  /**
   * Boire, manger, appliquer. Nul quand l'écran ne sait pas le faire.
   *
   * Enzo : « l'Eau de suif c'était pour se soigner je crois, pourquoi je n'ai
   * aucun bouton pour l'utiliser ? Ça doit être pareil pour tous les
   * consommables. » Ça l'était : la route serveur ne demande ni ville ni
   * échoppe, mais le seul bouton qui l'appelait vivait dans l'ATELIER, donc
   * dans une ville aux services ouverts. On se soigne après un combat, sur le
   * terrain, et c'est là qu'on cherche le bouton — dans ses sacs.
   */
  surConsommer?: () => void
  surFermer: () => void
}) {
  const bloque = empechement(o, classe, niveau)
  const prise = PRISE[o.famille]
  const estArme = o.degats.max > 0
  const stats = (Object.keys(o.bonus) as Stat[]).filter((s) => o.bonus[s])

  return (
    <div className="fiche-objet">
      <button className="fiche-fermer" onClick={surFermer} aria-label="fermer">×</button>

      <div className="fiche-tete">
        <IconeObjet objet={o} taille={54} />
        <div>
          <h4 className={`palier-${o.palier}`}>{o.nom}</h4>
          {/*
            La ligne qui répond à « je ne sais pas ce que c'est ». Le palier en
            toutes lettres, la famille, et la prise — dans cet ordre, parce que
            c'est celui dans lequel on se pose les questions.
          */}
          <p className="fiche-type">
            {NOM_PALIER[o.palier]}
            {o.categorie === 'equipement' && <> · {NOM_FAMILLE[o.famille]}</>}
            {prise && <> · à {prise === 'une_main' ? 'une main' : 'deux mains'}</>}
          </p>
          {o.categorie === 'equipement' && (
            <p className="fiche-ou">
              Se met en {NOM_EMPLACEMENT[o.emplacementCible].toLowerCase()}
              {prise === 'une_main' && ', dans l’une ou l’autre main'}
              {prise === 'deux_mains' && ' et occupe les deux mains'}
              {o.niveauRequis > 0 && ` · niveau ${o.niveauRequis}`}
            </p>
          )}
        </div>
      </div>

      {(estArme || stats.length > 0) && (
        <p className="fiche-chiffres">
          {estArme && <b>{o.degats.min} à {o.degats.max} de dégâts</b>}
          {/*
            Ce que la main gauche vaut vraiment.

            Une arme en seconde main ne frappe que pour MOITIÉ, et rien ne le
            disait : Enzo portait sa bonne dague à gauche et la mauvaise à
            droite, ce qui lui coûtait environ un tiers de ses dégâts sans
            qu'aucun écran ne l'indique. Une règle qu'on ne peut pas lire est
            un piège, pas une profondeur.
          */}
          {estArme && o.emplacement === 'arme_secondaire' && (
            <em className="fiche-moitie">
              en main gauche : {Math.round(o.degats.min * 0.5)} à {Math.round(o.degats.max * 0.5)},
              la seconde arme ne frappe que pour moitié
            </em>
          )}
          {stats.map((s) => (
            <span key={s}>{ABREGES[s]} +{o.bonus[s]}</span>
          ))}
          {o.amelioration > 0 && <i>+{o.amelioration} à la forge</i>}
        </p>
      )}

      {/* Ce qu'un consommable FAIT. Il n'a ni dégâts ni bonus à montrer. */}
      {o.effetTexte && <p className="fiche-effet">{o.effetTexte}</p>}
      {o.description && <p className="fiche-lore">{o.description}</p>}

      {/*
        L'empêchement AVANT le bouton, jamais après le clic. Un bouton qui ne
        fait rien se lit comme une panne ; un bouton absent avec sa raison écrite
        se lit comme une règle.
      */}
      {bloque ? (
        <p className="fiche-bloque">{bloque.phrase}</p>
      ) : (
        <div className="fiche-actions">
          {o.categorie === 'consommable' && surConsommer && (
            <button className="action" onClick={surConsommer}>
              {o.effetTexte ? 'Le prendre' : 'L’utiliser'}
            </button>
          )}
          {o.porte
            ? surRanger && <button className="action" onClick={surRanger}>Retirer</button>
            : surPorter &&
              o.emplacementsPossibles.map((e) => (
                <button key={e} className="action" onClick={() => surPorter(e)}>
                  {/*
                    Un bouton par destination réelle, et chacun DIT la sienne.

                    Le libellé ne nommait la destination que pour les mains ;
                    les deux doigts donnaient donc deux boutons « Porter »
                    indiscernables — le même défaut que celui qu'Enzo a signalé
                    (« quand je clique sur l'anneau, ça remplace le premier
                    slot »), simplement déplacé d'un cran. Dès qu'il y a un
                    choix, le choix se lit.
                  */}
                  {o.emplacementsPossibles.length > 1
                    ? `Porter · ${OU_PORTER[e] ?? 'ici'}`
                    : 'Porter'}
                </button>
              ))}
        </div>
      )}

      <p className="fiche-prix">
        {o.valeurMarchande > 0 ? `${o.valeurMarchande} or à la revente` : 'Ne se revend pas'}
      </p>
    </div>
  )
}

/** Ce que la classe accepte de tenir, pour l'écran de comparaison. Lecture seule. */
export function familleTenue(classe: Classe, o: ObjetDTO): boolean {
  return !MAINS.includes(o.emplacementCible) || MAINS_PAR_CLASSE[classe].includes(o.famille)
}
